import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';

// Kept in step with `duration-200` on the box below. A timer rather than `transitionend`, which
// never arrives when the run is cancelled or when the height it was handed turns out to be the one
// the box already had, and the box would then be left clipped at a fixed height for good.
const DURATION_MS = 200;

// Server-rendered closed boxes need the height in the markup: the effect below only runs after
// hydration, and until then the fields would show at full height. A module constant so React sees
// the same style object on every render and never writes over the height the effect owns.
const CLOSED_STYLE = { height: 0, overflow: 'hidden' } as const;

/**
 * Collapses and reveals its children, keeping them mounted.
 *
 * The height is animated in pixels, measured right before each run, rather than through
 * `grid-template-rows: 0fr → 1fr`. An `fr` row has to be resolved against the item's intrinsic
 * height, so that version re-measures the whole subtree on every frame; a pixel height only
 * re-lays-out this box and the siblings under it. The fade rides on `opacity` and `transform` on
 * the inner box rather than on the one whose size is changing, so it stays a compositor blend over
 * a raster taken once instead of a repaint of the subtree per frame.
 *
 * Once open the height goes back to `auto` and the box stops clipping, so it keeps following its
 * own content — a validation message appearing, a region select filling in — and nothing shaves
 * the focus rings off the fields at its edges.
 */
export function Reveal({ open, children, className }: { open: boolean; children: ReactNode; className?: string }) {
	const ref = useRef<HTMLDivElement>(null);
	// The value from the very first render, for the life of the component: reading the prop here
	// would rewrite the height the effect owns on every re-render.
	const initialOpen = useRef(open).current;
	// The first pass only puts the box where it already is. An element cannot transition from the
	// height it was server-rendered with, and animating on mount would replay the reveal on every
	// entry to the step.
	const isFirstPass = useRef(true);

	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return;

		if (isFirstPass.current) {
			isFirstPass.current = false;
			settle(element, open);
			return;
		}

		// Start from where the box actually is, so toggling mid-animation carries on from there
		// instead of snapping to one end first.
		element.style.overflow = 'hidden';
		element.style.height = `${element.getBoundingClientRect().height}px`;
		// Reading the height back commits that start value, which is what gives the target below
		// something to transition from within the same frame.
		void element.offsetHeight;
		element.style.height = open ? `${element.scrollHeight}px` : '0px';

		const timer = setTimeout(() => settle(element, open), DURATION_MS);
		return () => clearTimeout(timer);
	}, [open]);

	return (
		<div
			ref={ref}
			style={initialOpen ? undefined : CLOSED_STYLE}
			className={cn('transition-[height] duration-200 ease-out', className)}
			// The collapsed fields are still in the DOM, and clipping them does not take them out of
			// the tab order or the accessibility tree. Without this a keyboard or screen reader user
			// tabs through every hidden field on the way past.
			inert={!open}
		>
			<div
				className="transition-[opacity,transform] duration-200 ease-out"
				style={{ opacity: open ? 1 : 0, transform: open ? 'none' : 'translateY(-4px)' }}
			>
				{children}
			</div>
		</div>
	);
}

function settle(element: HTMLDivElement, open: boolean) {
	element.style.height = open ? 'auto' : '0px';
	element.style.overflow = open ? 'visible' : 'hidden';
}
